import { AlertCircle, Check, Sparkles } from "lucide-react";

import { ALL, useRecommendationFilters } from "../hooks/useRecommendationFilters";
import { EFFORT_CHIP_COLORS, EFFORT_FILTER_OPTIONS, EFFORT_LABELS } from "../utils/effort";
import { PRIORITY_ICON_COLORS, PRIORITY_OPTIONS } from "../utils/priority";
import {
  RECOMMENDATION_TYPE_LABELS,
  SCORING_CATEGORY_COLORS,
  SCORING_CATEGORY_LABELS,
  SCORING_CATEGORY_OPTIONS,
  RECOMMENDATION_TYPE_OPTIONS as TYPE_OPTIONS,
} from "../utils/recommendationLabels";
import Chip from "./Chip";
import PriorityBadge from "./PriorityBadge";
import RecommendationEmptyState from "./RecommendationEmptyState";
import RecommendationFilterBar from "./RecommendationFilterBar";

const STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "completed", label: "Completed" },
];

// One descriptor per dropdown, consumed by useRecommendationFilters: how a
// recommendation is tested against the selected value, and which option it counts
// towards. Filtering, counting and rendering all read this list, so each filter is
// defined in exactly one place.
const FILTERS = [
  {
    key: "status",
    label: "Status",
    allLabel: "All Statuses",
    options: STATUS_OPTIONS,
    matches: (rec, value) =>
      value === ALL || (value === "completed" ? Boolean(rec.completed) : !rec.completed),
    valueOf: (rec) => (rec.completed ? "completed" : "pending"),
  },
  {
    key: "priority",
    label: "Priority",
    allLabel: "All Priorities",
    options: PRIORITY_OPTIONS,
    matches: (rec, value) => value === ALL || rec.impact === value,
    valueOf: (rec) => rec.impact,
  },
  {
    key: "type",
    label: "Type",
    allLabel: "All Types",
    options: TYPE_OPTIONS,
    matches: (rec, value) => value === ALL || rec.recommendation_type === value,
    valueOf: (rec) => rec.recommendation_type,
  },
  {
    key: "scoringCategory",
    label: "Scoring Category",
    allLabel: "All Categories",
    options: SCORING_CATEGORY_OPTIONS,
    matches: (rec, value) => value === ALL || rec.scoring_category === value,
    valueOf: (rec) => rec.scoring_category,
  },
  {
    key: "effort",
    label: "Effort",
    allLabel: "All Efforts",
    options: EFFORT_FILTER_OPTIONS,
    matches: (rec, value) => value === ALL || rec.effort === value,
    valueOf: (rec) => rec.effort,
  },
];

const RecommendationsView = ({
  ref,
  recommendations,
  onToggleComplete,
  onShowGuide,
  onStartAutoFix,
}) => {
  const { filtered, options, values, hasActiveFilter, setFilter, clearFilters } =
    useRecommendationFilters(recommendations, FILTERS);

  return (
    <div ref={ref} className="bg-surface-container-lowest p-6 rounded-3xl shadow-cloud">
      <RecommendationFilterBar
        filters={FILTERS}
        values={values}
        options={options}
        hasActiveFilter={hasActiveFilter}
        onChange={setFilter}
        onClear={clearFilters}
        shownCount={filtered.length}
        totalCount={recommendations.length}
        noun="recommendations"
      />
      {filtered.length === 0 ? (
        <RecommendationEmptyState
          totalCount={recommendations.length}
          hasActiveFilter={hasActiveFilter}
          emptyMessage="No recommendations for this audit yet."
          onClear={clearFilters}
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filtered.map((rec) => (
            <div
              key={rec.id}
              className={`p-5 rounded-xl border transition-all ${rec.completed ? "bg-green-50 border-green-300" : "bg-surface-container-low border-surface-container-high hover:border-primary/30"}`}
            >
              <div className="flex items-start gap-3 mb-2">
                <AlertCircle
                  className={`w-5 h-5 mt-0.5 flex-shrink-0 ${PRIORITY_ICON_COLORS[rec.impact] || PRIORITY_ICON_COLORS.Medium}`}
                />
                <h4 className="font-bold text-on-surface">{rec.description}</h4>
              </div>
              <div className="pl-8 mb-4 flex items-center gap-2 flex-wrap text-xs text-on-surface-variant">
                <PriorityBadge priority={rec.impact} />
                {(rec.scoring_category || rec.category) && (
                  <Chip color={SCORING_CATEGORY_COLORS[rec.scoring_category] || "neutral"}>
                    {SCORING_CATEGORY_LABELS[rec.scoring_category] ||
                      rec.scoring_category ||
                      rec.category}
                  </Chip>
                )}
                {rec.recommendation_type && (
                  <Chip color="neutral">
                    {RECOMMENDATION_TYPE_LABELS[rec.recommendation_type] || rec.recommendation_type}
                  </Chip>
                )}
                {rec.effort && (
                  <Chip color={EFFORT_CHIP_COLORS[rec.effort] || "neutral"}>
                    {EFFORT_LABELS[rec.effort] || rec.effort}
                  </Chip>
                )}
                {rec.completed && (
                  <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-700 flex items-center gap-1">
                    <Check className="w-3 h-3" /> Completed
                  </span>
                )}
              </div>
              <div className="pl-8 flex items-center justify-between">
                <button
                  onClick={() => onShowGuide(rec.issue_resolution_guide)}
                  className="text-sm font-semibold text-primary hover:underline"
                >
                  Implementation Guide
                </button>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onToggleComplete(rec.id)}
                    className="px-4 py-2 text-sm font-semibold rounded-lg border transition-colors bg-surface-container-low text-on-surface border-surface-container-high hover:bg-surface-container-high"
                  >
                    {rec.completed ? "Undo" : "Mark Complete"}
                  </button>
                  {!rec.completed && (
                    <button
                      onClick={() => onStartAutoFix(rec)}
                      className="px-4 py-2 text-sm font-semibold bg-gradient-to-br from-primary to-primary-container text-on-primary hover:opacity-90 rounded-lg transition-colors flex items-center gap-2"
                    >
                      <Sparkles className="w-4 h-4" />
                      Suggested Changes
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RecommendationsView;
